/**
 * Lightweight, non-blocking analytics tracker.
 *
 * Every call is fire-and-forget: we never await the request, never surface
 * errors to the UI, and never retry. The backend naturally dedupes repeated
 * events within the same (truck, event, session, day) tuple so the client
 * can call this liberally without worrying about inflating numbers.
 */
import { API } from "./api";
import { Capacitor } from "@capacitor/core";

// web | ios_app | android_app — stamped on every analytics payload so the
// admin dashboard can split usage by platform.
function clientPlatform() {
  try {
    const p = Capacitor.getPlatform();
    if (p === "ios") return "ios_app";
    if (p === "android") return "android_app";
  } catch { /* not in a Capacitor context */ }
  return "web";
}

const SESSION_KEY = "trucktrackr_session_id";

function uuid() {
  // RFC4122-ish fallback — tab-lifetime only, so cryptographic strength isn't
  // required. Prefer crypto.randomUUID when available.
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "s-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export function getSessionId() {
  if (typeof window === "undefined") return "";
  try {
    let id = window.sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = uuid();
      window.sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    // Private mode / storage disabled — generate a per-load id so events can
    // still be deduped within this page view.
    return uuid();
  }
}

/**
 * Send a tracking event. Safe to call with missing/empty truckId — we no-op
 * so callers don't need defensive checks of their own.
 */
export function track(eventType, truckId, meta = null) {
  if (!eventType) return;
  if (typeof fetch === "undefined") return;
  // Backwards compat: site-level events without truck context are
  // accepted (truckId=null/undefined). Backend dedupes per session/day
  // for those, same as `trackSiteEvent`.
  try {
    const payload = {
      truck_id: truckId || null,
      event_type: eventType,
      session_id: getSessionId(),
      platform: clientPlatform(),
    };
    // Attach document.referrer on every track call so the backend can
    // attribute each viewer to a source channel (google/facebook/email/etc.)
    // for the owner-facing "Where viewers came from" panel. Backend
    // collapses same-origin referrers to null automatically.
    try {
      if (typeof document !== "undefined" && document.referrer) {
        payload.referrer = document.referrer;
      }
    } catch {
      // ignore — referrer is best-effort
    }
    if (meta && typeof meta === "object") {
      // `meta` is opaque to the backend — it's stored verbatim for
      // ad-hoc analytics queries. Keep payloads small.
      payload.meta = meta;
    }
    const body = JSON.stringify(payload);
    fetch(`${API}/analytics/track`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      credentials: "include",
      keepalive: true, // survives nav / tab close
    }).catch(() => {
      // Swallow — analytics failures are silent by design.
    });
  } catch {
    // Never let tracking throw into the UI
  }
}

/**
 * Site-level event ping (no truck context). Used for the push-notification
 * funnel and any other customer-side UX events that aren't tied to a truck.
 * Backend dedupes per (event_type, session, day).
 */
export function trackSiteEvent(eventType, meta = null) {
  if (!eventType) return;
  if (typeof fetch === "undefined") return;
  try {
    const body = JSON.stringify({
      event_type: eventType,
      session_id: getSessionId(),
      meta,
      platform: clientPlatform(),
    });
    fetch(`${API}/analytics/site-event`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      credentials: "include",
      keepalive: true,
    }).catch(() => {});
  } catch {
    // Never throw into the UI
  }
}

/**
 * Site-wide page view ping. Call once per route change. Same fire-and-forget
 * pattern as track() — backend dedupes per (path, session, day) so calling
 * this on every navigation (including back/forward) is safe.
 */
export function pageview(path) {
  if (typeof fetch === "undefined") return;
  if (typeof window === "undefined") return;
  // Owner/admin self-traffic is filtered server-side; we still ping so the
  // session key gets stamped on the event row for downstream attribution.
  try {
    const body = JSON.stringify({
      path: path || window.location.pathname || "/",
      session_id: getSessionId(),
      // Capture document.referrer so backend can attribute the session
      // to a source channel (Google, Facebook, email, direct, etc.).
      // Best-effort: empty in many cases (HTTPS→HTTP transitions,
      // privacy browsers, rel=noreferrer) — the backend treats empty as
      // "Direct" and only uses the FIRST page_view per session anyway.
      referrer: (typeof document !== "undefined" && document.referrer) ? document.referrer : "",
      platform: clientPlatform(),
    });
    fetch(`${API}/analytics/page-view`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      credentials: "include",
      keepalive: true,
    }).catch(() => {});
  } catch {
    // Never throw into the UI
  }
}
